import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Mail, Instagram, ArrowRight } from "lucide-react";
import { useSite } from "@/context/SiteContext";

export default function Contact() {
  const { site } = useSite();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const handle = (site.instagram?.handle || "").replace(/^@/, "");

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || form.message.length < 5) {
      toast.error("Please fill everything out");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      setSent(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Message failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="pt-24 md:pt-32 pb-24 md:pb-32 min-h-screen">
      <div className="max-w-[1000px] mx-auto px-6 md:px-12 grid md:grid-cols-2 gap-14 items-start">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-stone-500 font-semibold mb-4">Contact</p>
          <h1 className="font-serif text-5xl md:text-6xl font-light tracking-tighter">
            Say <em className="italic text-stone-600">hello</em>.
          </h1>
          <p className="mt-6 text-lg font-light text-stone-700 leading-relaxed">
            Wholesale questions, press, partnerships, or just a note — we read every message.
          </p>
          <div className="mt-10 space-y-4 text-sm text-stone-600">
            <a href="mailto:hello@jenovacollective.co" className="flex items-center gap-3 hover:text-[#1A1C1A] transition-colors">
              <Mail strokeWidth={1.5} className="w-4 h-4" /> hello@jenovacollective.co
            </a>
            {handle && (
              <a href={`https://instagram.com/${handle}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 hover:text-[#1A1C1A] transition-colors">
                <Instagram strokeWidth={1.5} className="w-4 h-4" /> @{handle}
              </a>
            )}
          </div>
        </div>

        <div>
          {sent ? (
            <div data-testid="contact-sent" className="border border-[#E5E2DC] rounded-2xl p-8 bg-[#F4F1EA]">
              <p className="text-xs uppercase tracking-[0.3em] text-stone-500 font-semibold">Message sent</p>
              <h2 className="mt-3 font-serif text-3xl font-light tracking-tight">Thank you.</h2>
              <p className="mt-4 text-stone-600 font-light leading-relaxed">
                We'll be in touch shortly. Watch your inbox at <strong>{form.email}</strong>.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="contact-form" className="space-y-6">
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-stone-500">Name</label>
                <input
                  type="text"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="contact-name"
                  className="w-full mt-1 bg-transparent border-b border-stone-300 py-3 focus:border-[#1A1C1A] focus:outline-none"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-stone-500">Email</label>
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="contact-email"
                  className="w-full mt-1 bg-transparent border-b border-stone-300 py-3 focus:border-[#1A1C1A] focus:outline-none"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-stone-500">Message</label>
                <textarea
                  rows={5}
                  required
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  data-testid="contact-message"
                  className="w-full mt-1 bg-transparent border-b border-stone-300 py-3 focus:border-[#1A1C1A] focus:outline-none resize-none"
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit"
                className="inline-flex items-center gap-2 bg-[#1A1C1A] text-[#F9F9F6] hover:bg-[#3C3E3C] disabled:opacity-60 px-8 py-4 rounded-full font-sans text-sm tracking-wider uppercase transition-colors"
              >
                {submitting ? "Sending…" : "Send message"}
                {!submitting && <ArrowRight strokeWidth={1.5} className="w-4 h-4" />}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
